import { BRAND_IMAGE_TAG_META, VALID_BRAND_IMAGE_TAGS } from '@mondaygpt/constants'
import type { BrandImageTag } from '@mondaygpt/constants'
import { Button } from 'components'
import { Dialog, DialogContent } from 'components/Dialog'
import type { BrandImage } from 'modules/creative/types'
import React, { useState } from 'react'

interface BrandImageTagModalProps {
  image: BrandImage
  onClose: () => void
  onSave: (tags: BrandImageTag[]) => void
}

export const BrandImageTagModal: React.FC<BrandImageTagModalProps> = ({
  image,
  onClose,
  onSave,
}) => {
  const [selectedTags, setSelectedTags] = useState<BrandImageTag[]>(image.tags || [])

  const toggleTag = (tag: BrandImageTag) => {
    setSelectedTags(prev => (prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]))
  }

  return (
    <Dialog
      open
      onOpenChange={open => {
        if (!open) onClose()
      }}
    >
      <DialogContent>
        <div className="flex max-h-[calc(90vh-3rem)] flex-col">
          <div className="flex-1 space-y-5 overflow-y-auto">
            {/* Image preview */}
            <div className="aspect-video w-full overflow-hidden rounded-xl bg-gray-100">
              <img
                src={image.publicUrl || ''}
                alt="Brand image"
                className="h-full w-full object-cover"
              />
            </div>

            {/* Tag selection */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-900">Choose tags</h3>
              <div className="space-y-2">
                {VALID_BRAND_IMAGE_TAGS.map(tag => {
                  const meta = BRAND_IMAGE_TAG_META[tag]
                  const checked = selectedTags.includes(tag)
                  return (
                    <label
                      key={tag}
                      className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 transition-all ${
                        checked ? 'border-acid bg-acid/5' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleTag(tag)}
                        className="h-4 w-4 rounded border-gray-300 text-acid accent-[var(--acid)]"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900">{meta.label}</div>
                        <div className="text-xs text-gray-500">{meta.description}</div>
                      </div>
                    </label>
                  )
                })}
              </div>
            </div>
          </div>

          {/* Done button — sticky at the bottom */}
          <div className="sticky bottom-0 bg-white pt-5">
            <Button
              type="button"
              onClick={() => onSave(selectedTags)}
              className="w-full"
              variant="t3-primary"
              size="t3-md"
            >
              Done
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
